/**
 * A remote desktop in the browser (stage 4): noVNC on the session's socket,
 * shown FULL-WINDOW — a drawer is fine for a terminal and unusable for a
 * screen (owner, first real session, 2026-09-28).
 *
 * 🔴 noVNC is created in the SAME handler that opens the socket, on a detached
 * element, and the view only moves that element onto the page. The agent sends
 * RFB's version string the moment the host joins; a viewer made a render later
 * can miss it, and a handshake that misses its first line never completes. It
 * also sidesteps React's development double-mount, which a socket owned by an
 * effect does not survive (the shell learned that first).
 */

import type RFB from '@novnc/novnc';
import { Expand, Minimize, Scaling, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from './components/index.ts';
import type { OpenedSession } from './ShellTerminal.tsx';

export interface DesktopConnection {
  sessionId: string;
  ws: WebSocket;
  rfb: RFB;
  /** noVNC's own element, created detached and mounted by the view. */
  el: HTMLDivElement;
  /** The latest status line, and whoever is showing it now. */
  status: string;
  listeners: Set<(s: string) => void>;
}

/**
 * noVNC is imported here, on first use — ~180 KB nobody needs until they open
 * a desktop — and BEFORE the socket is opened, so it is listening from the
 * socket's first byte.
 */
export async function openDesktop(session: OpenedSession): Promise<DesktopConnection> {
  const { default: RFBClass } = await import('@novnc/novnc');
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // The ticket rides as a second subprotocol, never in the URL (see src/remote/policy.ts).
  const ws = new WebSocket(`${scheme}://${window.location.host}${session.path}`, [session.protocol, `ticket.${session.ticket}`]);
  ws.binaryType = 'arraybuffer';
  const el = document.createElement('div');
  el.className = 'remote-desktop-screen';
  const rfb = new RFBClass(el, ws, { shared: true });
  rfb.scaleViewport = true;
  rfb.resizeSession = false;
  rfb.qualityLevel = 6;
  // The agent draws the remote pointer into the picture, a frame behind the
  // mouse; without a local cursor too, the pointer over the screen is invisible
  // (noVNC hides it when the server sends no cursor shape). Owner, 2026-09-28.
  rfb.showDotCursor = true;
  const conn: DesktopConnection = { sessionId: session.id, ws, rfb, el, status: 'Connecting…', listeners: new Set() };
  // The status outlives whichever view shows it (the drawer hands over to the full-window view).
  const say = (s: string): void => {
    conn.status = s;
    for (const l of conn.listeners) l(s);
  };
  rfb.addEventListener('connect', () => say('Connected — the person at this computer sees a bar saying support is connected.'));
  rfb.addEventListener('disconnect', (e: Event) => {
    const clean = (e as CustomEvent<{ clean?: boolean }>).detail?.clean;
    say(clean === false ? 'The connection was lost.' : 'Session closed.');
  });
  return conn;
}

/**
 * The full-window view: the remote screen fills the browser, a slim bar on
 * top. Deliberately NOT a dialog — a modal would trap focus, and the keyboard
 * belongs to the remote computer (Esc included), so leaving is End session.
 */
export function DesktopWindow({
  connection,
  label,
  onEnd,
}: {
  connection: DesktopConnection;
  label: string;
  onEnd: () => void;
}): React.JSX.Element {
  const frame = useRef<HTMLDivElement>(null);
  const screen = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState(connection.status);
  const [fit, setFit] = useState(true);
  const [full, setFull] = useState(false);

  useEffect(() => {
    connection.listeners.add(setStatus);
    setStatus(connection.status);
    return () => {
      connection.listeners.delete(setStatus);
    };
  }, [connection]);

  useEffect(() => {
    const host = screen.current;
    if (host === null) return;
    if (connection.el.parentElement !== host) host.appendChild(connection.el);
    connection.rfb.scaleViewport = fit; // re-evaluated now it has a size
    connection.rfb.clipViewport = false;
    connection.rfb.focus();
  }, [connection, fit]);

  useEffect(() => {
    const onChange = (): void => setFull(document.fullscreenElement === frame.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  return (
    <div ref={frame} className="remote-desktop-window" role="region" aria-label={`Remote desktop: ${label}`}>
      <div className="remote-desktop-bar">
        <strong className="remote-desktop-title">{label}</strong>
        <span className="small remote-desktop-status" role="status" aria-live="polite">
          {status}
        </span>
        <Button
          size="sm"
          variant="ghost"
          icon={<Scaling size={14} />}
          title={fit ? 'Show at actual size (scroll to move around)' : 'Fit the whole screen in the window'}
          onClick={() => setFit((f) => !f)}
        >
          {fit ? 'Fit' : '1:1'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon={full ? <Minimize size={14} /> : <Expand size={14} />}
          onClick={() => {
            if (document.fullscreenElement !== null) void document.exitFullscreen();
            else void frame.current?.requestFullscreen();
          }}
        >
          {full ? 'Exit full screen' : 'Full screen'}
        </Button>
        <Button size="sm" onClick={() => connection.rfb.sendCtrlAltDel()}>
          Ctrl+Alt+Del
        </Button>
        <Button size="sm" variant="danger" icon={<Square size={14} />} onClick={onEnd}>
          End session
        </Button>
      </div>
      <div ref={screen} className="remote-desktop-view" role="application" aria-label="Remote screen" />
    </div>
  );
}
